import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectClubCapabilities } from '@/store/clubCapabilitiesSlice';
import { ClubCapability } from '@/constant/Club';
import { KeenIcon } from '@/components';
import { useGetCommunityByIdQuery } from '@/services/CommunityManagementService';
import {
  ICommunityData,
  CommunityStatus,
} from '@/types/community-management.types';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/card/card';
import { Tag, Users, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import PostListings from '@/pages/posts-management/PostsListings';
import CommunityUsersListings from '@/pages/posts-management/CommunityUsersListings';
import ReportedPostListings from '@/pages/posts-management/ReportedPostsListings';
import RequestListings from '../posts-management/RequestsListings';
import { Demo1LayoutContext, useDemo1Layout } from '@/layouts/demo1';
import EventPromotion from './components/EventPromotion';

const CommunityInfo: React.FC<{ community: ICommunityData | null }> = ({
  community,
}) => {
  if (!community) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No community data available.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      {/* --- Header --- */}
      <header
        className="relative mb-8 overflow-hidden rounded-2xl bg-slate-800 p-8 text-white shadow-lg"
        role="banner"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('/placeholder.svg?width=1200&height=400')`,
          }}
        />
        <div className="relative z-10 flex flex-col items-start gap-6">
          <div className="flex-grow">
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
              {community.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                  community.status === CommunityStatus.GLOBAL ||
                  CommunityStatus.CLUB_FORUM
                    ? 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80'
                    : 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80'
                }`}
                role="status"
                aria-label={`Community status: ${community.status}`}
              >
                {community.status.split('_').join(' ')}
              </div>
              {community.tags?.length ? (
                <div
                  role="list"
                  aria-label="Community tags"
                  className="flex flex-wrap gap-2"
                >
                  {community.tags.map((tag, i) => (
                    <div
                      key={i}
                      className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-blue-300 bg-blue-500/10 text-blue-200"
                      role="listitem"
                      aria-label={`Tag: ${tag}`}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {/* --- Main Content Grid --- */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* --- Left/Main Column --- */}
        <div className="space-y-8 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>About {community.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                {community.description || 'No description provided.'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* --- Right/Sidebar Column --- */}
        <div className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Community Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-6 pb-6">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium mb-1">Moderators</div>
                  <div
                    className="flex flex-wrap gap-2"
                    role="list"
                    aria-label="Community moderators"
                  >
                    {community.moderators && community.moderators.length > 0 ? (
                      community.moderators.map((mod, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-300 text-gray-700"
                          role="listitem"
                        >
                          {mod.name || mod.email}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted-foreground" role="listitem">
                        N/A
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <DetailItem
                icon={Users}
                label="Max Users"
                value={community.max_users ?? 'Unlimited'}
              />
              <DetailItem
                icon={Tag}
                label="Tags"
                value={
                  community.tags?.length
                    ? community.tags.join(', ')
                    : 'No tags listed'
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Helper component for detail items
const DetailItem: React.FC<{
  icon: React.ElementType;
  label: string;
  value?: string | number | null;
  isLink?: boolean | string;
}> = ({ icon: Icon, label, value, isLink = false }) => {
  if (!value) return null;

  const content =
    isLink && typeof value === 'string' ? (
      <a
        href={typeof isLink === 'string' ? isLink : value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline"
        aria-label={`${label}: ${value} (opens in new tab)`}
      >
        {value}
      </a>
    ) : (
      <span className="text-muted-foreground">{value}</span>
    );

  return (
    <div className="flex items-start gap-3">
      <Icon
        className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
      <div className="flex flex-col">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <div className="text-sm break-all">{content}</div>
      </div>
    </div>
  );
};

const PostsManagement = () => {
  return (
    <div className="space-y-6">
      <Card className="border-0 overflow-hidden">
        <CardContent>
          <PostListings />
        </CardContent>
      </Card>
    </div>
  );
};

const UsersManagement = () => {
  return (
    <div className="space-y-6">
      <Card className="border-0 overflow-hidden">
        <CardContent>
          <CommunityUsersListings />
        </CardContent>
      </Card>
    </div>
  );
};

const ReportedPostsManagement = ({
  community,
}: {
  community: ICommunityData | null;
}) => {
  return (
    <div className="space-y-6">
      <Card className="border-0 overflow-hidden">
        <CardContent>
          <ReportedPostListings community={community} />
        </CardContent>
      </Card>
    </div>
  );
};

const RequestsManagement = () => {
  return (
    <div className="space-y-6">
      <Card className="border-0 overflow-hidden">
        <CardContent>
          <RequestListings />
        </CardContent>
      </Card>
    </div>
  );
};

const baseNavItems = [
  { name: 'Community Management', href: '#', id: 'community-info' },
  { name: 'Posts Management', href: '#', id: 'posts-management' },
  { name: 'Reported Posts', href: '#', id: 'reported-posts-management' },
  { name: 'Requests Management', href: '#', id: 'requests-management' },
  { name: 'Users Management', href: '#', id: 'users-management' },
  { name: 'Event Promotion', href: '#', id: 'event-promotion' },
];

export default function CommunityDetails() {
  const location = useLocation();
  const { layout } = useDemo1Layout() as unknown as Demo1LayoutContext;
  const communityId = useSelector(
    (state: RootState) => state.auth.selectedCommunityId
  );
  const { data: communityResponse } = useGetCommunityByIdQuery(
    communityId || '',
    { skip: !communityId }
  );

  // Get club capabilities from Redux
  const clubCapabilities = useSelector(selectClubCapabilities);

  // Check if COMMUNITY_NEWS_EVENTS_BASIC capability is present
  const hasNewsEventsBasicCapability = useMemo(() => {
    if (!clubCapabilities || clubCapabilities.length === 0) {
      return false;
    }
    return clubCapabilities.includes(
      ClubCapability.COMMUNITY_NEWS_EVENTS_BASIC
    );
  }, [clubCapabilities]);

  // Check if COMMUNITY_MODERATION_BASIC capability is present
  const hasModerationBasicCapability = useMemo(() => {
    if (!clubCapabilities || clubCapabilities.length === 0) {
      return false;
    }
    return clubCapabilities.includes(ClubCapability.COMMUNITY_MODERATION_BASIC);
  }, [clubCapabilities]);

  const community: ICommunityData | null = communityResponse
    ? {
        id: communityResponse.id,
        name: communityResponse.name || '',
        description: communityResponse.description || '',
        moderator_id: communityResponse.moderator?.id || '',
        status:
          (communityResponse.status as CommunityStatus) ||
          CommunityStatus.GLOBAL,
        tags: communityResponse.tags || [],
        max_users: communityResponse.max_users,
        created_at: communityResponse.created_at || '',
        updated_at: communityResponse.updated_at || '',
        moderator: {
          id: communityResponse.moderator?.id || '',
          name:
            communityResponse.moderator?.name ||
            communityResponse.moderator?.email ||
            'Unknown User',
          email: communityResponse.moderator?.email || '',
        },
        moderators: communityResponse.moderators || [],
        moderator_ids: communityResponse.moderator_ids || [],
        user_role: communityResponse.user_role || '',
        is_yacht_club_community: communityResponse.is_yacht_club_community,
      }
    : null;

  const navItems = useMemo(() => {
    let items = [...baseNavItems];

    if (community?.is_yacht_club_community) {
      // Filter out requests-management and users-management for yacht club communities
      items = items.filter(
        (item) =>
          item.id !== 'requests-management' && item.id !== 'users-management'
      );

      // For yacht club communities, only show event-promotion if COMMUNITY_NEWS_EVENTS_BASIC capability is present
      if (!hasNewsEventsBasicCapability) {
        items = items.filter((item) => item.id !== 'event-promotion');
      }

      // For yacht club communities, only show posts-management and reported-posts-management if COMMUNITY_MODERATION_BASIC capability is present
      if (!hasModerationBasicCapability) {
        items = items.filter(
          (item) =>
            item.id !== 'posts-management' &&
            item.id !== 'reported-posts-management'
        );
      }
    }
    // If is_yacht_club_community is false, event-promotion, posts-management, and reported-posts-management are included (no permission check needed)

    return items;
  }, [
    community?.is_yacht_club_community,
    hasNewsEventsBasicCapability,
    hasModerationBasicCapability,
  ]);

  const urlSearchParams = new URLSearchParams(location.search);
  const tabFromUrl = urlSearchParams.get('tab');

  const [activeTab, setActiveTab] = useState(
    tabFromUrl && navItems.some((item) => item.id === tabFromUrl)
      ? tabFromUrl
      : navItems[0].id
  );

  // Update URL when tab changes
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.get('tab') !== activeTab) {
      currentParams.set('tab', activeTab);
      window.history.replaceState(
        null,
        '',
        `${location.pathname}?${currentParams.toString()}`
      );
    }
  }, [activeTab, location.pathname, location.search]);

  // Mobile pager: cycle through tabs one at a time
  const currentIndex = navItems.findIndex((item) => item.id === activeTab);
  const goPrevTab = () => {
    const prevIndex = (currentIndex - 1 + navItems.length) % navItems.length;
    const id = navItems[prevIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };
  const goNextTab = () => {
    const nextIndex = (currentIndex + 1) % navItems.length;
    const id = navItems[nextIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'community-info':
        return <CommunityInfo community={community} />;
      case 'posts-management':
        return <PostsManagement />;
      case 'users-management':
        return <UsersManagement />;
      case 'reported-posts-management':
        return <ReportedPostsManagement community={community} />;
      case 'requests-management':
        return <RequestsManagement />;
      case 'event-promotion':
        return <EventPromotion community={community} />;
      default:
        return <CommunityInfo community={community} />;
    }
  };

  return (
    <div className="community-page w-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <div className="mt-3">
            <Link
              to="/community-management"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              aria-label="Back to community management"
            >
              <KeenIcon
                icon="black-left"
                className="text-xl"
                aria-hidden="true"
              />
            </Link>
          </div>
          <header
            className={`md:border-b border-gray-200 community-header ${layout.options.sidebar.collapse ? 'header-collapsed' : ''}`}
          >
            <nav
              className="-mb-px flex space-x-6 overflow-x-auto px-1"
              aria-label="Tabs"
              style={{ maxWidth: 'calc(100vw - 200px)' }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={`?tab=${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(item.id);
                    // Update URL without navigation
                    window.history.pushState(null, '', `?tab=${item.id}`);
                  }}
                  role="tab"
                  aria-selected={activeTab === item.id}
                  aria-controls={`community-tabpanel-${item.id}`}
                  className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-1 flex-shrink-0 min-w-fit contrast:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${
                    activeTab === item.id
                      ? 'border-blue-500 text-blue-600 contrast:text-blue-500 contrast:border-blue-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            {/* Mobile-only pager: hidden by default, shown at 320px via CSS */}
            <div
              className="community-mobile-nav hidden items-center justify-between"
              aria-label="Tabs mobile"
            >
              <button
                type="button"
                className="nav-arrow prev"
                onClick={goPrevTab}
                aria-label="Previous section"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="current-tab" aria-current="page">
                {navItems[currentIndex]?.name}
              </div>
              <button
                type="button"
                className="nav-arrow next"
                onClick={goNextTab}
                aria-label="Next section"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </header>
        </div>
        <div
          className="py-8"
          role="tabpanel"
          id={`community-tabpanel-${activeTab}`}
          aria-label={`${navItems[currentIndex]?.name || 'Section'} content`}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
